import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import LoadingIcon from "components/Loading/LoadingIcon";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePatchDateOfInjuryByIdMutation } from "services/doi-api";
import type { ServiceDateOfInjury } from "types/DataTypes";
import { useToken } from "utils/context";

import type { ICDObject, ICDUpdateData } from "./icdAdapter";
import { serviceDoiToIcdObject } from "./icdAdapter";
import ICDCodingForm from "./ICDCodingForm";

export const MAX_ICD_CODES = 19;

type ICDValue = {
  code: string;
  description: string;
};

const buildPendingFieldValues = (
  field: string | null,
  value: unknown,
): Record<string, unknown> => {
  if (
    field &&
    typeof value === "object" &&
    value !== null &&
    "code" in value &&
    "description" in value
  ) {
    const icdObj = value as ICDValue;
    return {
      [field]: icdObj.code,
      [`${field}_verbose`]: icdObj.description,
    };
  }

  if (field) {
    return { [field]: value };
  }

  if (value && typeof value === "object") {
    return { ...(value as Record<string, unknown>) };
  }

  return {};
};

const isPendingFieldSynced = (
  field: string,
  icdData: ICDObject,
  pendingFieldValues: Record<string, unknown>,
): boolean => {
  const pending = pendingFieldValues[field];

  if (field === "icd_indicator") {
    return icdData.icd_indicator === pending;
  }

  if (field === "alleged_cause_of_injury") {
    const expected = (pending as string | null) || null;
    const actual = icdData.alleged_cause_of_injury || null;
    return actual === expected;
  }

  if (field.endsWith("_accepted")) {
    const codeField = field.replace("_accepted", "");
    const codeIndex = parseInt(codeField.split("_").pop() || "0");
    return icdData.icd_codes[codeIndex]?.accepted === pending;
  }

  if (field.startsWith("icd_diag_code_")) {
    const codeIndex = parseInt(field.split("_").pop() || "0");
    const entry = icdData.icd_codes[codeIndex];
    const expectedCode = (pending as string | null) || null;
    const expectedVerbose = pendingFieldValues[`${field}_verbose`];

    if (expectedVerbose !== undefined) {
      return (
        (entry?.code || null) === expectedCode &&
        (entry?.verbose || null) ===
          ((expectedVerbose as string | null) || null)
      );
    }

    if (!expectedCode) {
      return !entry?.code;
    }

    return entry?.code === expectedCode;
  }

  return true;
};

type ICDCodingBlockProps = {
  data?: ServiceDateOfInjury | ICDObject;
  isLoading?: boolean;
  onSave?: (data: ICDUpdateData) => Promise<void>;
  showAcceptanceButtons?: boolean;
};

/**
 * ICDCodingBlock component for managing ICD codes
 * Can accept different data types which are converted to a common ICDObject format
 */
const ICDCodingBlock = ({
  data,
  isLoading = false,
  onSave,
  showAcceptanceButtons = true,
}: ICDCodingBlockProps) => {
  const [showResetModal, setShowResetModal] = useState(false);
  const [activeSlots, setActiveSlots] = useState<number[]>([]);
  const [savingFields, setSavingFields] = useState<Record<string, boolean>>({});
  const [pendingFieldValues, setPendingFieldValues] = useState<
    Record<string, unknown>
  >({});

  const { accessToken } = useToken();
  const [patchDateOfInjury] = usePatchDateOfInjuryByIdMutation();

  const clearPendingFields = useCallback((fields: string[]) => {
    if (fields.length === 0) {
      return;
    }

    setPendingFieldValues((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        delete next[field];
        if (
          field.startsWith("icd_diag_code_") &&
          !field.endsWith("_accepted")
        ) {
          delete next[`${field}_verbose`];
        }
      });
      return next;
    });
  }, []);

  const getSavingFieldKeys = useCallback(
    (field: string | null, value: unknown): string[] => {
      if (field) {
        return [field];
      }

      if (value && typeof value === "object") {
        return Object.keys(value as Record<string, unknown>).filter(
          (key) => !key.endsWith("_verbose"),
        );
      }

      return [];
    },
    [],
  );

  const setFieldsSaving = useCallback((fields: string[], isSaving: boolean) => {
    if (fields.length === 0) {
      return;
    }

    setSavingFields((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        if (isSaving) {
          next[field] = true;
        } else {
          delete next[field];
        }
      });
      return next;
    });
  }, []);

  // convert input data to ICDObject format
  const icdData = useMemo(() => {
    if (!data) return null;

    // if it's already an ICDObject (from a Lien or Claim)
    if ("icd_codes" in data) {
      return data as ICDObject;
    }

    // if it's a ServiceDateOfInjury
    if ("date_of_injury_id" in data) {
      return serviceDoiToIcdObject(data as ServiceDateOfInjury);
    }

    // Default fallback
    return null;
  }, [data]);

  // get all the icd codes that are currently in the data
  const icdCodes = useMemo(() => {
    if (!icdData) return [];

    const existingCodes = Object.keys(icdData.icd_codes).map(
      (index) => `icd_diag_code_${index}`,
    );

    const activeCodes = activeSlots.map((slot) => `icd_diag_code_${slot}`);
    return [...new Set([...existingCodes, ...activeCodes])];
  }, [icdData, activeSlots]);

  useEffect(() => {
    if (!icdData) {
      return;
    }

    const syncedFields = Object.keys(savingFields).filter((field) =>
      isPendingFieldSynced(field, icdData, pendingFieldValues),
    );

    if (syncedFields.length === 0) {
      return;
    }

    setFieldsSaving(syncedFields, false);
    clearPendingFields(syncedFields);
  }, [
    icdData,
    savingFields,
    pendingFieldValues,
    setFieldsSaving,
    clearPendingFields,
  ]);

  const handleSave = useCallback(
    async (field: string | null, value: unknown) => {
      if (!icdData?.id || !accessToken) return;

      const fieldsToTrack = getSavingFieldKeys(field, value);
      const pendingValues = buildPendingFieldValues(field, value);

      setFieldsSaving(fieldsToTrack, true);
      setPendingFieldValues((prev) => ({ ...prev, ...pendingValues }));

      try {
        // use custom onSave if provided
        if (onSave) {
          if (
            field &&
            typeof value === "object" &&
            value !== null &&
            "code" in value &&
            "description" in value
          ) {
            // extract the code number from field (e.g., "icd_diag_code_1" -> 1)
            const codeNumber = parseInt(field.split("_").pop() || "0");
            if (!codeNumber) {
              throw new Error(`Invalid ICD code field name: ${field}`);
            }
            const icdObj = value as ICDValue;
            await onSave({
              [field]: icdObj.code,
              [`${field}_verbose`]: icdObj.description,
            });
            return;
          }
          // for updating indicator and alleged cause of injury or bulk updates
          await onSave(
            field
              ? ({ [field]: value } as ICDUpdateData)
              : (value as ICDUpdateData),
          );
          return;
        }

        // for updating indicator and alleged cause of injury
        await patchDateOfInjury({
          id: icdData.id.toString(),
          data: field ? { [field]: value } : value,
        });
      } catch (error) {
        console.error("Failed to update DOI:", error);
        KaboodleToaster.error("Failed to update ICD data");
        setFieldsSaving(fieldsToTrack, false);
        clearPendingFields(fieldsToTrack);
        throw error;
      }
    },
    [
      icdData?.id,
      patchDateOfInjury,
      onSave,
      accessToken,
      getSavingFieldKeys,
      setFieldsSaving,
      clearPendingFields,
    ],
  );

  const handleAddCode = useCallback(async () => {
    if (!icdData) return;

    // find the next available code number
    const usedCodeNumbers = Object.keys(icdData.icd_codes).map(Number);
    const nextCodeNumber = Array.from({ length: MAX_ICD_CODES })
      .map((_, i) => i + 1)
      .find(
        (num) => !usedCodeNumbers.includes(num) && !activeSlots.includes(num),
      );

    if (!nextCodeNumber) {
      KaboodleToaster.error(`Maximum of ${MAX_ICD_CODES} codes allowed`);
      return;
    }

    try {
      // add the code number to the active slots
      setActiveSlots((prev) => [...prev, nextCodeNumber]);
      // save the code number to the data
      await handleSave(`icd_diag_code_${nextCodeNumber}`, null);
    } catch (error) {
      setActiveSlots((prev) => prev.filter((slot) => slot !== nextCodeNumber));
      console.error("Failed to add ICD code:", error);
      KaboodleToaster.error("Failed to add ICD code");
    }
  }, [icdData, activeSlots, handleSave]);

  // remove an ICD code from the data
  const handleRemoveCode = useCallback(
    async (codeName: string) => {
      const codeNumber = parseInt(codeName.split("_").pop() || "0");
      if (!codeNumber) return;

      try {
        setActiveSlots((prev) => prev.filter((slot) => slot !== codeNumber));
        await handleSave(null, {
          [codeName]: null,
          [`${codeName}_verbose`]: null,
        });
        KaboodleToaster.success("ICD code removed successfully");
      } catch (error) {
        console.error("Failed to remove ICD code:", error);
        KaboodleToaster.error("Failed to remove ICD code");
      }
    },
    [handleSave],
  );

  const handleReset = useCallback(async () => {
    if (!icdData?.id) return;

    try {
      const clearData = {
        icd_indicator: null,
        alleged_cause_of_injury: null,
        ...Object.fromEntries(
          Array.from({ length: MAX_ICD_CODES }).flatMap((_, i) => [
            [`icd_diag_code_${i + 1}`, null],
            [`icd_diag_code_${i + 1}_verbose`, null],
          ]),
        ),
      };
      await handleSave(null, clearData);
      setActiveSlots([]);
      setShowResetModal(false);
      KaboodleToaster.success("All ICD codes reset successfully");
    } catch (error) {
      console.error("Failed to reset ICD codes:", error);
      KaboodleToaster.error("Failed to reset ICD codes");
    }
  }, [handleSave, icdData?.id]);

  // check if there's any ICD data to reset
  const hasIcdData = Boolean(
    icdData?.alleged_cause_of_injury || icdCodes.length > 0,
  );

  const canAddNewCode = Boolean(icdData?.icd_indicator);

  if (isLoading || !icdData) {
    return (
      <div className="icd-coding-block">
        <div className="data-block-header">ICD Coding</div>
        <div className="flex jc-c p-4">
          <LoadingIcon size="small" showText={false} />
        </div>
      </div>
    );
  }

  return (
    <div className="icd-coding-block">
      <ICDCodingForm
        data={icdData}
        onSave={handleSave}
        onRemoveCode={handleRemoveCode}
        readonly={false}
        icdCodes={icdCodes}
        canAddNewCode={canAddNewCode}
        hasIcdData={hasIcdData}
        onAddCode={handleAddCode}
        onShowResetModal={() => setShowResetModal(true)}
        showAcceptanceButtons={showAcceptanceButtons}
        savingFields={savingFields}
      />

      {showResetModal && (
        <KaboodleModal
          isOpen={showResetModal}
          onClose={() => setShowResetModal(false)}
          footer={
            <div className="flex jc-c">
              <Button onClick={handleReset} className="delete">
                Confirm Reset
              </Button>
              <Button onClick={() => setShowResetModal(false)}>Cancel</Button>
            </div>
          }
        >
          <Card heading="Reset ICD Data">
            <p>Are you sure you want to reset all ICD data?</p>
          </Card>
        </KaboodleModal>
      )}
    </div>
  );
};

export default ICDCodingBlock;
